import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import {
  IndividualCreatePayload,
  IndividualRecord,
  IndividualShort,
  IndividualUpdatePayload,
  Protocol,
  TransformIndividualRecord,
} from '@/api/vaccination/registry/types';
import { PageableRequest, PageableResponse } from '@/shared/api/types';
import {
  INDIVIDUAL,
  INDIVIDUAL_ARCHIVE,
  INDIVIDUAL_BY_ID,
  INDIVIDUAL_UTERUS,
} from '@/shared/vaccination/api';
import { mapSortToQueryParameter } from '@/utils/utils';

import { vaccinationRegistryTags } from './tags';

export const vaccinationRegistryApi = createApi({
  reducerPath: 'vaccinationRegistryApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(vaccinationRegistryTags),
  endpoints: (builder) => ({
    getRegistryRecords: builder.query<PageableResponse<IndividualShort>, PageableRequest>(
      {
        query: (payload) => ({
          url: INDIVIDUAL,
          method: 'GET',
          params: {
            ...payload.filters,
            page: payload?.paging?.page,
            size: payload?.paging?.size,
            sort: mapSortToQueryParameter(payload?.sort),
          },
        }),
        providesTags: [vaccinationRegistryTags.REGISTRY],
      },
    ),
    getArchiveRecords: builder.query<PageableResponse<IndividualShort>, PageableRequest>({
      query: (payload) => ({
        url: INDIVIDUAL_ARCHIVE,
        method: 'GET',
        params: {
          ...payload.filters,
          page: payload?.paging?.page,
          size: payload?.paging?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [vaccinationRegistryTags.ARCHIVE],
    }),
    getRegistryRecord: builder.query<TransformIndividualRecord, { id: string }>({
      query: ({ id }) => ({
        url: INDIVIDUAL_BY_ID(id),
        method: 'GET',
      }),
      transformResponse: (response: IndividualRecord) => {
        const protocolsByType = {} as Record<string, Protocol[]>;
        response?.protocolTypes?.forEach(
          (item) => (protocolsByType[item.eventProtocolTypeId] = item.protocols),
        );

        return {
          ...response,
          protocolsByType,
          updatedByName: response.updatedByUser?.fullName || '',
          createdByName: response.createdByUser?.fullName || '',
        };
      },
      providesTags: [vaccinationRegistryTags.REGISTRY_RECORD],
    }),
    createRegistryRecord: builder.mutation<void, IndividualCreatePayload>({
      query: (record) => ({
        url: INDIVIDUAL,
        method: 'POST',
        body: record,
      }),
      invalidatesTags: [
        vaccinationRegistryTags.REGISTRY,
        vaccinationRegistryTags.ARCHIVE,
        vaccinationRegistryTags.UTERUS_LIST,
      ],
    }),
    updateRegistryRecord: builder.mutation<void, IndividualUpdatePayload>({
      query: ({ id, ...body }) => ({
        url: INDIVIDUAL_BY_ID(id),
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: [
        vaccinationRegistryTags.REGISTRY,
        vaccinationRegistryTags.ARCHIVE,
        vaccinationRegistryTags.REGISTRY_RECORD,
      ],
    }),
    deleteRegistryRecord: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: INDIVIDUAL_BY_ID(id),
        method: 'DELETE',
      }),
      invalidatesTags: [
        vaccinationRegistryTags.ARCHIVE,
        vaccinationRegistryTags.REGISTRY_RECORD,
      ],
    }),
    getUterus: builder.query<string[], void>({
      query: () => ({
        url: INDIVIDUAL_UTERUS,
        method: 'GET',
      }),
      providesTags: [vaccinationRegistryTags.UTERUS_LIST],
    }),
  }),
});

export const {
  useGetRegistryRecordsQuery,
  useGetArchiveRecordsQuery,
  useGetRegistryRecordQuery,
  useCreateRegistryRecordMutation,
  useUpdateRegistryRecordMutation,
  useDeleteRegistryRecordMutation,
  useGetUterusQuery,
} = vaccinationRegistryApi;
